import { Controller } from "@hotwired/stimulus";
import {
  errorDetail,
  formatDate,
  formatPln,
  getAllJSON,
  sendJSON,
} from "../api.js";
import { getActLabel, setActLabel } from "../icons.js";

// Moje leady: zamówienia + dostawy z DRF; pobieranie ZIP + "wyślij ponownie".
// Hasło do ZIP nigdy nie występuje w danych (idzie SMS-em) — 0-PII na stronie.
export default class extends Controller {
  static targets = ["rows", "status", "bought", "spent", "paidCount"];
  static values = {
    url: String, // /api/orders/
    deliveriesUrl: String, // /api/deliveries/
  };

  static STATUS_BADGE = {
    pending: ["new", "oczekuje"],
    paid: ["warm", "opłacone"],
    delivered: ["sent", "dostarczone"],
    failed: ["hot", "błąd"],
    refunded: ["hot", "zwrócone"],
  };

  connect() {
    this.load();
  }

  async load() {
    let orderList;
    let deliveryList;
    try {
      // Wszystkie strony: metryki (sumy pieniędzy) i join zamówienie↔dostawa nie mogą
      // być liczone z pierwszych 25 rekordów.
      [orderList, deliveryList] = await Promise.all([
        getAllJSON(this.urlValue),
        getAllJSON(this.deliveriesUrlValue),
      ]);
    } catch {
      this.#status("Nie udało się załadować zamówień. Odśwież stronę.");
      return;
    }
    const deliveryByOrder = new Map(deliveryList.map((d) => [d.order_code, d]));

    this.#renderMetrics(orderList);
    this.rowsTarget.textContent = "";
    if (!orderList.length) {
      this.#status("Brak zamówień. Kup pierwsze leady w panelu klienta.");
      return;
    }
    this.#status(null);
    const template = document.getElementById("order-row-template");
    for (const order of orderList) {
      const row = template.content.firstElementChild.cloneNode(true);
      const items = order.items ?? [];
      const first = items[0]?.category_path ?? "—";
      row.querySelector('[data-field="cat"]').textContent =
        items.length > 1 ? `${first} +${items.length - 1}` : first;
      row.querySelector('[data-field="number"]').textContent =
        `ZAM-${order.code}`;
      row.querySelector('[data-field="date"]').textContent = formatDate(
        order.created,
      );
      row.querySelector('[data-field="qty"]').textContent = items.length;

      const [kind, label] = this.constructor.STATUS_BADGE[order.status] ?? [
        "new",
        order.status,
      ];
      const badge = row.querySelector('[data-field="badge"]');
      badge.className = `ls-badge ls-badge--${kind}`;
      badge.textContent = label;

      const delivery = deliveryByOrder.get(order.code);
      if (delivery?.download_url) {
        const dl = row.querySelector('[data-field="download"]');
        dl.hidden = false;
        dl.setAttribute("data-orders-url-param", delivery.download_url);
      }
      if (delivery) {
        const resendBtn = row.querySelector('[data-field="resend"]');
        resendBtn.hidden = false;
        resendBtn.setAttribute("data-orders-id-param", delivery.id);
      }
      this.rowsTarget.appendChild(row);
    }
  }

  // Pobranie ZIP przez fetch (nie goły <a href>): daje stan ładowania i przyjazną obsługę
  // wyścigu — link mógł zwietrzeć między listą a klikiem (410 wygasł / 409 jeszcze niegotowy).
  async download(event) {
    const btn = event.currentTarget;
    const url = event.params.url;
    if (!url) return;
    const original = getActLabel(btn);
    btn.disabled = true;
    setActLabel(btn, "Pobieranie…");
    this.#status(null);
    try {
      const res = await fetch(url, { credentials: "same-origin" });
      if (!res.ok) {
        this.#status(this.#downloadError(res.status));
        return;
      }
      this.#saveBlob(await res.blob(), this.#filenameFrom(res));
    } catch {
      this.#status(
        "Nie udało się pobrać paczki. Sprawdź połączenie i spróbuj ponownie.",
      );
    } finally {
      btn.disabled = false;
      setActLabel(btn, original);
    }
  }

  async resend(event) {
    const btn = event.currentTarget;
    btn.disabled = true;
    const res = await sendJSON(
      "POST",
      `${this.deliveriesUrlValue}${event.params.id}/resend/`,
    );
    if (res.ok) {
      setActLabel(btn, "Wysłano ✓");
      return;
    }
    btn.disabled = false;
    setActLabel(
      btn,
      res.status === 429 ? "Limit wyczerpany" : "Błąd — spróbuj znów",
    );
    if (res.status === 429) btn.disabled = true;
    this.#status(errorDetail(res.data, "Nie udało się ponowić wysyłki."));
  }

  #downloadError(status) {
    if (status === 410)
      return "Link do pobrania wygasł. Użyj „Wyślij ponownie”, aby dostać nową paczkę.";
    if (status === 409)
      return "Paczka nie jest jeszcze gotowa — spróbuj za chwilę.";
    return "Nie udało się pobrać paczki. Spróbuj ponownie.";
  }

  #filenameFrom(res) {
    const disposition = res.headers?.get?.("Content-Disposition") ?? "";
    const match = /filename="?([^"]+?)"?$/.exec(disposition);
    return match ? match[1] : "pakiet.zip";
  }

  #saveBlob(blob, filename) {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  }

  #renderMetrics(orderList) {
    const paidOrders = orderList.filter(
      (o) => o.status === "paid" || o.status === "delivered",
    );
    const bought = paidOrders.reduce(
      (sum, o) => sum + (o.items?.length ?? 0),
      0,
    );
    const spent = paidOrders.reduce(
      (sum, o) => sum + Number(o.gross_pln || 0),
      0,
    );
    if (this.hasBoughtTarget) this.boughtTarget.textContent = bought;
    if (this.hasSpentTarget) this.spentTarget.textContent = formatPln(spent);
    if (this.hasPaidCountTarget)
      this.paidCountTarget.textContent = paidOrders.length;
  }

  #status(message) {
    if (!this.hasStatusTarget) return;
    this.statusTarget.hidden = message === null;
    if (message !== null) this.statusTarget.textContent = message;
  }
}
